import turfBbox from '@turf/bbox';
import turfCenterOfMass from '@turf/center-of-mass';
import * as turf from '@turf/helpers';
import { DateTime } from 'luxon';

import Status from '@/common/models/lineitem/LineItemStatus.enum';
import { setProp, uniq } from '@/globals';

import * as types from './mutation-types';

const makeMutation =
  (field, entityName, updateTimestamp = false, transform = null, postProcess = null) =>
  (state, change) => {
    const entity = change[entityName];
    let newValue = change.value;

    if (transform !== null) {
      newValue = transform(newValue);
    }
    setProp(entity, field, newValue);

    if (postProcess !== null) {
      postProcess(entity, newValue);
    }

    if (updateTimestamp && entity) {
      entity.timestamp = DateTime.local();
    }
  };

const updateCreativeDraft = (creative) => {
  if (creative.state !== Status.DRAFT) {
    creative.state = Status.DRAFT;
  }
};

const mutations = {
  // ACCOUNT
  [types.UPDATE_ACCOUNT_NAME]: makeMutation('name', 'account', true),

  // ADVANCED REPORT EXECUTION
  [types.UPDATE_ADVANCEDREPORTEXECUTION_TYPE]: makeMutation('reportType', 'advancedReport'),
  [types.UPDATE_ADVANCEDREPORTEXECUTION_BREAKDOWN]: makeMutation('definition.breakdown', 'advancedReport'),
  [types.UPDATE_ADVANCEDREPORTEXECUTION_DATES]: makeMutation('definition.dates', 'advancedReport', true),
  [types.UPDATE_ADVANCEDREPORTEXECUTION_TIMEZONE]: makeMutation('definition.timezone', 'advancedReport', true),
  [types.UPDATE_ADVANCEDREPORTEXECUTION_EMAILS]: makeMutation('emails', 'advancedReport'),

  // ADVANCED REPORT SCHEDULES
  [types.UPDATE_ADVANCEDREPORTSCHEDULE_REPORTTYPE]: makeMutation('reportType', 'advancedReportSchedule', true),
  [types.UPDATE_ADVANCEDREPORTSCHEDULE_REPORTPARAMETERS]: makeMutation(
    'reportParameters',
    'advancedReportSchedule',
    true,
  ),
  [types.UPDATE_ADVANCEDREPORTSCHEDULE_EXECUTIONTIMEZONE]: makeMutation(
    'executionTimezone',
    'advancedReportSchedule',
    true,
  ),
  [types.UPDATE_ADVANCEDREPORTSCHEDULE_FIRSTEVENT]: makeMutation(
    'firstLocalExecutionTime',
    'advancedReportSchedule',
    true,
  ),
  [types.UPDATE_ADVANCEDREPORTSCHEDULE_LASTEVENT]: makeMutation(
    'lastLocalExecutionTime',
    'advancedReportSchedule',
    true,
  ),
  [types.UPDATE_ADVANCEDREPORTSCHEDULE_RECURRINGPERIOD]: makeMutation(
    'recurringPeriod',
    'advancedReportSchedule',
    true,
  ),
  [types.UPDATE_ADVANCEDREPORTSCHEDULE_DAYOFMONTH]: makeMutation('dayOfMonth', 'advancedReportSchedule', true),
  [types.UPDATE_ADVANCEDREPORTSCHEDULE_NOTIFICATIONS]: makeMutation(
    'notificationEmails',
    'advancedReportSchedule',
    true,
  ),

  // ADVERTISER
  [types.UPDATE_ADVERTISER_NAME]: makeMutation('name', 'advertiser', true),
  [types.UPDATE_ADVERTISER_DOMAIN]: makeMutation('domain', 'advertiser', true),
  [types.UPDATE_ADVERTISER_MINSECONDSBETWEENPLAYS]: makeMutation('minSecondsBetweenPlays', 'advertiser', true),

  // AUDIENCE FILE
  [types.UPDATE_AUDIENCEFILE_NAME]: makeMutation('name', 'audienceFile', true),

  // AUDIENCE LOGIC
  [types.UPDATE_AUDIENCELOGIC_NAME]: makeMutation('name', 'audienceLogic', true),
  [types.UPDATE_AUDIENCELOGIC_DATAPROVIDERID]: makeMutation('dataProviderID', 'audienceLogic', true),
  [types.UPDATE_AUDIENCELOGIC_DATAMARKETPLACEID]: makeMutation('dataMarketplaceID', 'audienceLogic', true),
  [types.UPDATE_AUDIENCELOGIC_EXTERNALDEMOGRAPHICIDS]: makeMutation('externalDemographicIDs', 'audienceLogic', true),
  [types.UPDATE_AUDIENCELOGIC_LOGICALEXPRESSION]: makeMutation('logicalExpression', 'audienceLogic', true),
  [types.UPDATE_AUDIENCELOGIC_AUDIENCERULES]: makeMutation('audienceRules', 'audienceLogic', true),
  [types.UPDATE_AUDIENCERULE_KEY]: makeMutation('key', 'audienceRule', true),
  [types.UPDATE_AUDIENCERULE_NAME]: makeMutation('name', 'audienceRule', true),
  [types.UPDATE_AUDIENCERULE_SEGMENTS]: makeMutation('segments', 'audienceRule', true),
  [types.UPDATE_AUDIENCERULE_CUSTOM_GROUP]: makeMutation('audienceRuleCustomGroup', 'audienceRule', false),
  [types.UPDATE_AUDIENCERULE_CUSTOM_GROUP_NAME]: makeMutation('name', 'audienceRuleCustomGroup', false),
  [types.UPDATE_AUDIENCERULE_CUSTOM_GROUP_S3PATH]: makeMutation('s3Path', 'audienceRuleCustomGroup', false),
  [types.UPDATE_AUDIENCERULE_GEOFENCEGROUPID]: makeMutation('geofenceGroupID', 'audienceRule', true),
  [types.UPDATE_AUDIENCERULE_EXTERNALDEMOGRAPHICID]: makeMutation('externalDemographicID', 'audienceRule', true),
  [types.UPDATE_AUDIENCERULE_DAYPART]: makeMutation('daypart', 'audienceRule', true),
  [types.UPDATE_AUDIENCERULE_ISCUSTOMDAYPART]: makeMutation('isCustomDaypart', 'audienceRule', true),
  [types.UPDATE_AUDIENCERULE_ISNEGATED]: makeMutation('isNegated', 'audienceRule', true),
  [types.UPDATE_AUDIENCERULE_DATAPROVIDER_ID]: makeMutation('dataProviderID', 'audienceRule', true),
  [types.UPDATE_AUDIENCEGROUP_OPERATOR]: makeMutation('operator', 'audienceRule', true),
  [types.UPDATE_AUDIENCERULE_LOOKBACKWINDOWS]: makeMutation('audienceLookbackWindows', 'audienceRule', true),
  [types.UPDATE_AUDIENCELOOKBACKWINDOW_PERIODTYPE]: makeMutation('periodType', 'AudienceLookBackWindow', true),
  [types.UPDATE_AUDIENCELOOKBACKWINDOW_ISRELATIVE]: makeMutation('isRelative', 'AudienceLookBackWindow', true),
  [types.UPDATE_AUDIENCELOOKBACKWINDOW_FROMDATE]: makeMutation('fromDate', 'AudienceLookBackWindow', true),
  [types.UPDATE_AUDIENCELOOKBACKWINDOW_TODATE]: makeMutation('toDate', 'AudienceLookBackWindow', true),
  [types.UPDATE_AUDIENCELOOKBACKWINDOW_TIMEPERIOD]: makeMutation('timePeriod', 'AudienceLookBackWindow', true),
  [types.UPDATE_AUDIENCELOOKBACKWINDOW_CANDIDATEFREQUENCY]: makeMutation(
    'candidateFrequency',
    'AudienceLookBackWindow',
    true,
  ),
  [types.UPDATE_NODES]: makeMutation('nodes', 'entity'),
  [types.UPDATE_NODE_OPERATOR]: makeMutation('operator', 'entity'),

  // CAMPAIGNS
  [types.UPDATE_CAMPAIGN_NAME]: makeMutation('name', 'campaign', true),
  [types.UPDATE_CAMPAIGN_ADVERTISER_ID]: makeMutation('advertiserID', 'campaign', true),
  [types.UPDATE_CAMPAIGN_ACCOUNT_ID]: makeMutation('accountID', 'campaign', true),
  [types.UPDATE_CAMPAIGN_STARTDATE]: makeMutation('startDate', 'campaign', true),
  [types.UPDATE_CAMPAIGN_ENDDATE]: makeMutation('endDate', 'campaign', true),
  [types.UPDATE_CAMPAIGN_BUDGET]: makeMutation('budget', 'campaign', true),
  [types.UPDATE_CAMPAIGN_BUDGET_CAP]: makeMutation('budgetCap', 'campaign', true),
  [types.UPDATE_CAMPAIGN_DAILYCAP]: makeMutation('dailyCap', 'campaign', true),
  [types.UPDATE_CAMPAIGN_INSERTION_ORDER_BUDGET]: makeMutation('insertionOrderBudget', 'campaign', true),
  [types.UPDATE_CAMPAIGN_FLIGHTS]: makeMutation('flights', 'campaign', true),
  [types.UPDATE_CAMPAIGN_PACINGBUDGET]: makeMutation('pacingBudget', 'campaign', true),
  [types.UPDATE_CAMPAIGN_PACINGBUDGETMODE]: makeMutation('pacingBudgetMode', 'campaign', true),
  [types.UPDATE_CAMPAIGN_PACINGMODIFIEDAT]: makeMutation('pacingModifiedAt', 'campaign', true),
  [types.UPDATE_CAMPAIGN_PACINGSTRATEGY]: makeMutation('pacingStrategy', 'campaign', true),
  [types.UPDATE_CAMPAIGN_NOTES]: makeMutation('notes', 'campaign', true),
  [types.UPDATE_CAMPAIGN_PROPERTIES]: makeMutation('campaignProperties', 'campaign', true),
  [types.UPDATE_CAMPAIGN_MINSECONDSBETWEENPLAYS]: makeMutation('minSecondsBetweenPlays', 'campaign', true),
  [types.UPDATE_CAMPAIGN_LINE_ITEMS]: makeMutation('lineItems', 'campaign'),
  [types.UPDATE_CAMPAIGN_DELIVERY_HOURLY_STATS]: makeMutation('hourlyDeliveryStats', 'campaign'),
  [types.UPDATE_CAMPAIGN_PRIORITY]: makeMutation('priority', 'campaign', true),
  [types.UPDATE_CAMPAIGN_MARGINS]: makeMutation('margins', 'campaign', true),
  [types.UPDATE_CAMPAIGN_INTERNAL_PROPERTIES]: makeMutation('campaignInternalProperties', 'campaign', true),
  [types.UPDATE_CAMPAIGN_PLAN_ID]: makeMutation('planID', 'campaign', true),
  [types.UPDATE_CAMPAIGN_REACH_AND_FREQUENCY_REQUESTED]: makeMutation('reachAndFrequencyStatus', 'campaign', true),
  [types.UPDATE_CAMPAIGN_SHAREDENTITY]: makeMutation('sharedEntity', 'campaign', true),
  [types.UPDATE_CAMPAIGN_TYPE]: makeMutation('campaignType', 'campaign', true),
  [types.UPDATE_CAMPAIGN_IS_TEST]: makeMutation('isTestCampaign', 'campaign', true),
  [types.UPDATE_CAMPAIGN_ADVERTISER_BUDGET]: makeMutation('advertiserBudget', 'campaign', true),

  // CREATIVES
  [types.UPDATE_CREATIVE_ADVERTISER_ID]: makeMutation('advertiserID', 'creative', true, null, updateCreativeDraft),
  [types.UPDATE_CREATIVE_APPROVAL_COMMENT]: makeMutation('approvalComment', 'creative', true),
  [types.UPDATE_CREATIVE_CREATIVEFILES]: makeMutation('creativeFiles', 'creative'),
  [types.UPDATE_CREATIVE_CREATIVEFORMATEXCLUSIONS]: makeMutation('creativeFormatExclusions', 'creative', true),
  [types.UPDATE_CREATIVE_CREATIVEFORMATS]: makeMutation('creativeFormats', 'creative', true),
  [types.UPDATE_CREATIVE_CREATIVEORIENTATIONEXCLUSIONS]: makeMutation(
    'creativeOrientationExclusions',
    'creative',
    true,
  ),
  [types.UPDATE_CREATIVE_CREATIVEORIENTATIONS]: makeMutation('creativeOrientations', 'creative', true),
  [types.UPDATE_CREATIVE_CREATIVEPROPERTIES]: makeMutation(
    'creativeProperties',
    'creative',
    true,
    (val) => val?.filter((x) => x.key.trim() !== ''),
    updateCreativeDraft,
  ),
  [types.UPDATE_CREATIVE_CREATIVETRACKINGURLS]: makeMutation('trackingUrls', 'creative', true),
  [types.UPDATE_CREATIVE_CREATIVETYPE]: makeMutation('creativeType', 'creative', true),
  [types.UPDATE_CREATIVE_DAYPART]: makeMutation('daypart', 'creative', true),
  [types.UPDATE_CREATIVE_PRECISE_DAYPART]: makeMutation('preciseDaypart', 'creative', true),
  [types.UPDATE_CREATIVE_GEOFENCE_GROUP_IDS]: makeMutation('geofenceGroupIDs', 'creative', true),
  [types.UPDATE_CREATIVE_GEOFENCE_GROUP_EXCLUSION_IDS]: makeMutation('geofenceGroupExclusionIDs', 'creative', true),
  [types.UPDATE_CREATIVE_IAB_CATEGORY_IDS]: makeMutation('iabCategories', 'creative', true, null, updateCreativeDraft),
  [types.UPDATE_CREATIVE_IS_FALLBACK]: makeMutation('isFallback', 'creative', true, null, updateCreativeDraft),
  [types.UPDATE_CREATIVE_IS_MOBILE]: makeMutation('isMobile', 'creative', true, null, updateCreativeDraft),
  [types.UPDATE_CREATIVE_LANGUAGE_IDS]: makeMutation('languageIDs', 'creative', true, null, updateCreativeDraft),
  [types.UPDATE_CREATIVE_LINEITEMS]: makeMutation('lineItems', 'creative'),
  [types.UPDATE_CREATIVE_LOCATIONIDS]: makeMutation('locationIDs', 'creative', true),
  [types.UPDATE_CREATIVE_LOCATIONEXCLUSIONIDS]: makeMutation('locationExclusionIDs', 'creative', true),
  [types.UPDATE_CREATIVE_MARKET_IDS]: makeMutation('marketIDs', 'creative', true),
  [types.UPDATE_CREATIVE_MARKETEXCLUSIONIDS]: makeMutation('marketExclusionIDs', 'creative', true),
  [types.UPDATE_CREATIVE_MATCHING_CREATIVE_SIZES]: makeMutation('matchingCreativeSizes', 'creative'),
  [types.UPDATE_CREATIVE_MEDIATYPEIDS]: makeMutation('mediaTypeIDs', 'creative', true),
  [types.UPDATE_CREATIVE_MEDIATYPEEXCLUSIONIDS]: makeMutation('mediaTypeExclusionIDs', 'creative', true),
  [types.UPDATE_CREATIVE_MISSING_CREATIVE_SIZES]: makeMutation('missingCreativeSizes', 'creative'),
  [types.UPDATE_CREATIVE_NAME]: makeMutation('name', 'creative', true, null),
  [types.UPDATE_CREATIVE_NETWORKIDS]: makeMutation('networkIDs', 'creative', true),
  [types.UPDATE_CREATIVE_NETWORKEXCLUSIONIDS]: makeMutation('networkExclusionIDs', 'creative', true),
  [types.UPDATE_CREATIVE_OVERRIDEDEFAULTDURATION]: makeMutation('overrideDefaultDuration', 'creative', true),
  [types.UPDATE_CREATIVE_RUNSTATE]: makeMutation('runState', 'creative'),
  [types.UPDATE_CREATIVE_SITEIDS]: makeMutation('siteIDs', 'creative', true),
  [types.UPDATE_CREATIVE_SITEEXCLUSIONIDS]: makeMutation('siteExclusionIDs', 'creative', true),
  [types.UPDATE_CREATIVE_UNITTAGEXCLUSIONS]: makeMutation('unitTagExclusions', 'creative', true),
  [types.UPDATE_CREATIVE_UNITTAGS]: makeMutation('unitTags', 'creative', true),
  [types.UPDATE_CREATIVE_STATE]: makeMutation('state', 'creative', true, null, (entity, val) => {
    switch (val) {
      case Status.SUBMITTED:
        entity.submittedOn = DateTime.local();
        break;
      case Status.CANCELLED:
        entity.cancelledOn = DateTime.local();
        break;
      default:
        break;
    }
  }),
  [types.UPDATE_CREATIVE_TARGETING_CRITERIA]: makeMutation('creativeTargetingCriteria', 'creative', true),
  [types.UPDATE_CREATIVE_UNITIDS]: makeMutation('unitIDs', 'creative', true),
  [types.UPDATE_CREATIVE_UNITEXCLUSIONIDS]: makeMutation('unitExclusionIDs', 'creative', true),
  [types.UPDATE_CREATIVE_UNITPACKIDS]: makeMutation('unitPackIDs', 'creative', true),
  [types.UPDATE_CREATIVE_UNITPACKEXCLUSIONIDS]: makeMutation('unitPackExclusionIDs', 'creative', true),

  // DEMOGRAPHICS
  [types.UPDATE_DEMOGRAPHIC_NAME]: makeMutation('name', 'demographic', true),

  // EXTERNAL EVENTS
  [types.UPDATE_CUSTOM_EVENT_NAME]: makeMutation('name', 'customEvent', true),
  [types.UPDATE_CUSTOM_EVENT_CODE]: makeMutation('code', 'customEvent', true),
  [types.UPDATE_CUSTOM_EVENT_CATEGORYNAME]: makeMutation('categoryName', 'customEvent', true),

  // FLIGHTS
  [types.UPDATE_FLIGHT_STARTDATE]: makeMutation('startDate', 'flight', true),
  [types.UPDATE_FLIGHT_ENDDATE]: makeMutation('endDate', 'flight', true),
  [types.UPDATE_FLIGHT_GOAL]: makeMutation('goal', 'flight', true),

  // GEOFENCE GROUPS
  [types.UPDATE_GEOFENCE_GROUP_DESCRIPTION]: makeMutation('description', 'geofenceGroup', true),
  [types.UPDATE_GEOFENCE_GROUP_NAME]: makeMutation('name', 'geofenceGroup', true),
  [types.UPDATE_GEOFENCE_GROUP_TAGS]: makeMutation('tags', 'geofenceGroup', true),
  [types.UPDATE_GEOFENCE_GROUP_GEOFENCES]: makeMutation('geofences', 'geofenceGroup', true),
  [types.UPDATE_GEOFENCE_MAPBOXDRAWID]: makeMutation('mapboxDrawID', 'geofence', true),
  [types.UPDATE_GEOFENCE_LAT]: makeMutation('lat', 'geofence', true),
  [types.UPDATE_GEOFENCE_LNG]: makeMutation('lng', 'geofence', true),
  [types.UPDATE_GEOFENCE_SWLAT]: makeMutation('boundsSwLat', 'geofence', true),
  [types.UPDATE_GEOFENCE_SWLNG]: makeMutation('boundsSwLng', 'geofence', true),
  [types.UPDATE_GEOFENCE_NELAT]: makeMutation('boundsNeLat', 'geofence', true),
  [types.UPDATE_GEOFENCE_NELNG]: makeMutation('boundsNeLng', 'geofence', true),
  [types.UPDATE_GEOFENCE_RADIUS]: makeMutation('radius', 'geofence', true),
  [types.UPDATE_GEOFENCE_RADIUS_UNIT]: makeMutation('radiusUnit', 'geofence', true),
  [types.UPDATE_GEOFENCE_RADIUS_IN_UNIT]: makeMutation('radiusInUnit', 'geofence', true),
  [types.UPDATE_GEOFENCE_EXTRUSION]: makeMutation('extrusion', 'geofence', true),
  [types.UPDATE_GEOFENCE_ISPOINT]: makeMutation('isPoint', 'geofence', true),
  [types.UPDATE_GEOFENCE_GEOFENCEGROUPID]: makeMutation('geofenceGroupID', 'geofence', true),
  [types.UPDATE_GEOFENCE_GEOJSON]: makeMutation('geoJson', 'geofence', true, null, (entity, val) => {
    const geometry = typeof val === 'string' ? JSON.parse(val) : val;
    let feature = null;

    if (geometry.type === 'Polygon') {
      feature = turf.polygon(geometry.coordinates);
    } else {
      feature = turf.multiPolygon(geometry.coordinates);
    }
    const center = turfCenterOfMass(feature);
    const bbox = turfBbox(feature.geometry);
    [entity.lng] = center.geometry.coordinates;
    [, entity.lat] = center.geometry.coordinates;
    [entity.boundsSwLng, entity.boundsSwLat, entity.boundsNeLng, entity.boundsNeLat] = bbox;
  }),

  // LINEITEM
  [types.UPDATE_LINEITEM_NAME]: makeMutation('name', 'lineItem', true),
  [types.UPDATE_LINEITEM_BIDDING_TYPE]: makeMutation('biddingType', 'lineItem', true),
  [types.UPDATE_LINEITEM_CAMPAIGN_ID]: makeMutation('campaignID', 'lineItem', true),
  [types.UPDATE_LINEITEM_CHANNEL_TYPE]: makeMutation('channelType', 'lineItem', true),
  [types.UPDATE_LINEITEM_CAMPAIGN]: makeMutation('campaign', 'lineItem', true),
  [types.UPDATE_LINEITEM_STARTDATE]: makeMutation('startDate', 'lineItem', true),
  [types.UPDATE_LINEITEM_ENDDATE]: makeMutation('endDate', 'lineItem', true),
  [types.UPDATE_LINEITEM_EXTERNAL_ID]: makeMutation('externalID', 'lineItem', true),
  [types.UPDATE_LINEITEM_ID]: makeMutation('id', 'lineItem', false),
  [types.UPDATE_LINEITEM_PROPERTIES]: makeMutation('lineItemProperties', 'lineItem', true),
  [types.UPDATE_LINEITEM_TARGETING_CRITERIA]: makeMutation('lineItemTargetingCriteria', 'lineItem', true),
  [types.UPDATE_LINEITEM_NOTES]: makeMutation('notes', 'lineItem', true),
  [types.UPDATE_LINEITEM_CADENCE]: makeMutation('cadence', 'lineItem', true),
  [types.UPDATE_LINEITEM_CADENCE_SMOOTH_CATCHUP]: makeMutation('cadenceSmoothCatchup', 'lineItem', true),
  [types.UPDATE_LINEITEM_BUDGET]: makeMutation('budget', 'lineItem', true),
  [types.UPDATE_LINEITEM_BUDGET_MODE]: makeMutation('budgetMode', 'lineItem', true),
  [types.UPDATE_LINEITEM_BUY_FROM_DEALS_ENABLED]: makeMutation('buyFromDealsEnabled', 'lineItem', true),
  [types.UPDATE_LINEITEM_CPM]: makeMutation('CPM', 'lineItem', true),
  [types.UPDATE_LINEITEM_TARGETIMPRESSIONS]: makeMutation('targetImpressions', 'lineItem', true),
  [types.UPDATE_LINEITEM_ADDURATION]: makeMutation('adDuration', 'lineItem', true),
  [types.UPDATE_LINEITEM_MARGINS]: makeMutation('margins', 'lineItem', true),
  [types.UPDATE_LINEITEM_MAXBIDCPM]: makeMutation('maxBidCPM', 'lineItem', true),
  [types.UPDATE_LINEITEM_MINBIDCPM]: makeMutation('minBidCPM', 'lineItem', true),
  [types.UPDATE_LINEITEM_PRIORITY]: makeMutation('priority', 'lineItem', true),
  [types.UPDATE_LINEITEM_MINSECONDSBETWEENPLAYS]: makeMutation('minSecondsBetweenPlays', 'lineItem', true),
  [types.UPDATE_LINEITEM_NETWORKFREQUENCYCAPS]: makeMutation('networkFrequencyCaps', 'lineItem', true),
  [types.UPDATE_LINEITEM_MEDIATYPEIDS]: makeMutation('mediaTypeIDs', 'lineItem', true),
  [types.UPDATE_LINEITEM_MEDIATYPEEXCLUSIONIDS]: makeMutation('mediaTypeExclusionIDs', 'lineItem', true),
  [types.UPDATE_LINEITEM_DAYPART]: makeMutation('daypart', 'lineItem', true),
  [types.UPDATE_LINEITEM_DEMOGRAPHICIDS]: makeMutation('demographicIDs', 'lineItem', true),
  [types.UPDATE_LINEITEM_PRODUCTIDS]: makeMutation('productIDs', 'lineItem', true),
  [types.UPDATE_LINEITEM_LANGUAGEIDS]: makeMutation('languageIDs', 'lineItem', true),
  [types.UPDATE_LINEITEM_LANGUAGEEXCLUSIONIDS]: makeMutation('languageExclusionIDs', 'lineItem', true),
  [types.UPDATE_LINEITEM_LOCATIONIDS]: makeMutation('locationIDs', 'lineItem', true),
  [types.UPDATE_LINEITEM_LOCATIONEXCLUSIONIDS]: makeMutation('locationExclusionIDs', 'lineItem', true),
  [types.UPDATE_LINEITEM_SITEIDS]: makeMutation('siteIDs', 'lineItem', true),
  [types.UPDATE_LINEITEM_SITEEXCLUSIONIDS]: makeMutation('siteExclusionIDs', 'lineItem', true),
  [types.UPDATE_LINEITEM_UNITIDS]: makeMutation('unitIDs', 'lineItem', true),
  [types.UPDATE_LINEITEM_UNITEXCLUSIONIDS]: makeMutation('unitExclusionIDs', 'lineItem', true),
  [types.UPDATE_LINEITEM_UNITPACKIDS]: makeMutation('unitPackIDs', 'lineItem', true),
  [types.UPDATE_LINEITEM_UNITPACKEXCLUSIONIDS]: makeMutation('unitPackExclusionIDs', 'lineItem', true),
  [types.UPDATE_LINEITEM_UNITTAGEXCLUSIONS]: makeMutation('unitTagExclusions', 'lineItem'),
  [types.UPDATE_LINEITEM_UNITTAGS]: makeMutation('unitTags', 'lineItem'),
  [types.UPDATE_LINEITEM_GEOFENCE_GROUP_IDS]: makeMutation('geofenceGroupIDs', 'lineItem', true),
  [types.UPDATE_LINEITEM_GEOFENCE_GROUP_EXCLUSION_IDS]: makeMutation('geofenceGroupExclusionIDs', 'lineItem', true),
  [types.UPDATE_LINEITEM_MARKET_IDS]: makeMutation('marketIDs', 'lineItem', true),
  [types.UPDATE_LINEITEM_MARKETEXCLUSIONIDS]: makeMutation('marketExclusionIDs', 'lineItem', true),
  [types.UPDATE_LINEITEM_MEDIAOWNERIDS]: makeMutation('mediaOwnerIDs', 'lineItem', true),
  [types.UPDATE_LINEITEM_CREATIVEFORMATEXCLUSIONS]: makeMutation('creativeFormatExclusions', 'lineItem', true),
  [types.UPDATE_LINEITEM_CREATIVEFORMATS]: makeMutation('creativeFormats', 'lineItem', true),
  [types.UPDATE_LINEITEM_CREATIVEORIENTATIONEXCLUSIONS]: makeMutation(
    'creativeOrientationExclusions',
    'lineItem',
    true,
  ),
  [types.UPDATE_LINEITEM_CREATIVEORIENTATIONS]: makeMutation('creativeOrientations', 'lineItem', true),
  [types.UPDATE_LINEITEM_CREATIVEIDS]: makeMutation('creativeIDs', 'lineItem', true),
  [types.UPDATE_LINEITEM_CREATIVES](state, change) {
    const { lineItem } = change;
    const newValue = change.value;
    lineItem.creatives = newValue;
    lineItem.creativeIDs = newValue.map((x) => x.id);
    lineItem.timestamp = DateTime.local();
  },
  [types.UPDATE_LINEITEM_NETWORKIDS]: makeMutation('networkIDs', 'lineItem', true),
  [types.UPDATE_LINEITEM_NETWORKEXCLUSIONIDS]: makeMutation('networkExclusionIDs', 'lineItem', true),
  [types.UPDATE_LINEITEM_DELIVERY_HOURLY_STATS]: makeMutation('hourlyDeliveryStats', 'lineItem'),
  [types.UPDATE_LINEITEM_DAILY_GOAL_CAP]: makeMutation('dailyGoalCap', 'lineItem'),
  [types.UPDATE_LINEITEM_DAILY_DELIVERY_HOURLY_STATS]: makeMutation('dailyHourlyDeliveryStats', 'lineItem'),
  [types.UPDATE_LINEITEM_MISSING_CREATIVE_SIZES]: makeMutation('missingCreativeSizes', 'lineItem'),
  [types.UPDATE_LINEITEM_DELIVERY_MODE]: makeMutation('deliveryMode', 'lineItem', true),
  [types.UPDATE_LINEITEM_PURCHASE_TYPE]: makeMutation('purchaseType', 'lineItem', true),
  [types.UPDATE_LINEITEM_COMPANION_GROUP_NAME]: makeMutation('companionGroupName', 'lineItem', true),
  [types.UPDATE_LINEITEM_COMPANION_ORDER]: makeMutation('companionOrder', 'lineItem', true),
  [types.UPDATE_LINEITEM_HAS_INFINITE_BUDGET]: makeMutation('hasInfiniteBudget', 'lineItem', true),
  [types.UPDATE_LINEITEM_IABCATEGORIES]: makeMutation('iabCategories', 'lineItem', true),
  [types.UPDATE_LINEITEM_IABCATEGORYEXCLUSIONS]: makeMutation('iabCategoryExclusions', 'lineItem', true),
  [types.UPDATE_LINEITEM_IS_CPM_FROM_RATE_CARD]: makeMutation('isCPMFromRateCard', 'lineItem', true),
  [types.UPDATE_LINEITEM_IS_MEASUREMENT_REQUESTED]: makeMutation('isMeasurementRequested', 'lineItem', true),
  [types.UPDATE_LINEITEM_ATTACHED_MEASUREMENTS]: makeMutation('attachedMeasurements', 'lineItem', true),
  [types.UPDATE_LINEITEM_MIN_CONCENTRATION_FACTOR]: makeMutation('minConcentrationFactor', 'lineItem', true),
  [types.UPDATE_LINEITEM_MIN_CONCENTRATION_VALUE]: makeMutation('minConcentrationValue', 'lineItem', true),
  [types.UPDATE_LINEITEM_DELIVERY_CURVE]: makeMutation('deliveryCurve', 'lineItem', true),
  [types.UPDATE_LINEITEM_DISCOUNTS]: makeMutation('lineItemDiscounts', 'lineItem', true),
  [types.UPDATE_LINEITEM_STATE]: makeMutation('state', 'lineItem', true, null, (entity, val) => {
    switch (val) {
      case Status.SUBMITTED:
        entity.submittedOn = DateTime.local();
        break;
      case Status.CANCELLED:
        entity.cancelledOn = DateTime.local();
        break;
      default:
        break;
    }
  }),
  [types.UPDATE_LINEITEM_RUNSTATE]: makeMutation('runState', 'lineItem', true),
  [types.UPDATE_LINEITEM_APPROVAL_COMMENT]: makeMutation('approvalComment', 'lineItem', true),
  [types.UPDATE_LINEITEM_PACKAGE]: makeMutation('packageInternal', 'lineItem', true),
  [types.UPDATE_LINEITEM_PACKAGE_ID]: makeMutation('packageID', 'lineItem', true),
  [types.UPDATE_LINEITEM_PACKAGEGROUP_ID]: makeMutation('packageGroupID', 'lineItem', true),
  [types.UPDATE_LINEITEM_PACKAGEGROUP_DEALS]: makeMutation('packageGroupDealsIDs', 'lineItem'),
  [types.UPDATE_LINEITEM_POSTAL_CODES_FILES]: makeMutation('lineItemPostalCodesFiles', 'lineItem', true),
  [types.UPDATE_LINEITEM_ENTITY_FEES]: makeMutation('entityFees', 'lineItem'),
  [types.UPDATE_LINEITEM_ADD_FEATURE_KEY](state, change) {
    const { lineItem, value } = change;
    const valueTransient = value.filter((v) => typeof v === 'object' && v.transient).map((v) => v.key ?? v);
    const valueFixed = value.filter((v) => typeof v === 'string' || !v.transient).map((v) => v.key ?? v);
    lineItem.transientFeatureKeys = uniq([...lineItem.transientFeatureKeys, ...valueTransient]);
    lineItem.featureKeys = uniq([...lineItem.featureKeys, ...valueFixed]);
  },
  [types.UPDATE_LINEITEM_REMOVE_FEATURE_KEY](state, change) {
    const { lineItem, value } = change;
    const valueTransient = value.filter((v) => typeof v === 'object' && v.transient).map((v) => v.key ?? v);
    const valueFixed = value.filter((v) => typeof v === 'string' || !v.transient).map((v) => v.key ?? v);
    lineItem.transientFeatureKeys = lineItem.transientFeatureKeys.filter((k) => valueTransient.indexOf(k) < 0);
    lineItem.featureKeys = lineItem.featureKeys.filter((k) => valueFixed.indexOf(k) < 0);
  },
  [types.UPDATE_LINEITEM_CPM_CALCULATOR]: makeMutation('cpmCalculator', 'lineItem', true),
  [types.UPDATE_LINEITEM_TIMESTAMP](state, change) {
    const { lineItem } = change;
    lineItem.timestamp = DateTime.local();
  },
  [types.UPDATE_LINEITEM_ESTIMATED_CPM]: makeMutation('estimatedCPM', 'lineItem'),
  [types.UPDATE_LINEITEM_ESTIMATED_AUDIENCE_CPM]: makeMutation('estimatedAudienceCPM', 'lineItem'),
  [types.UPDATE_LINEITEM_ESTIMATED_AVERAGE_CPM]: makeMutation('estimatedAverageCPM', 'lineItem'),
  [types.UPDATE_LINEITEM_ESTIMATED_AVERAGE_AUDIENCE_CPM]: makeMutation('estimatedAverageAudienceCPM', 'lineItem'),
  [types.UPDATE_LINEITEM_FLIGHT_RATIO]: makeMutation('flightRatio', 'lineItem', true),
  [types.UPDATE_EXCHANGE_FEE_OVERRIDE_TYPE]: makeMutation('exchangeFeeOverrideType', 'lineItem'),
  [types.UPDATE_EXCHANGE_FEE_OVERRIDE_VALUE]: makeMutation('exchangeFeeOverrideValue', 'lineItem'),
  [types.UPDATE_LINEITEM_ADVERTISER_BUDGET]: makeMutation('advertiserBudget', 'lineItem', true),
  [types.UPDATE_LINEITEM_IS_OFFLINE_ENABLED]: makeMutation('isOfflineEnabled', 'lineItem'),

  // MARGINS
  [types.UPDATE_MARGIN_NAME]: makeMutation('name', 'margin', true),

  // MOBILE LINEITEMS
  [types.UPDATE_MOBILE_LINEITEM_NAME]: makeMutation('name', 'mobileLineItem', true),
  [types.UPDATE_MOBILE_LINEITEM_CAMPAIGN_ID]: makeMutation('campaignID', 'mobileLineItem', true),
  [types.UPDATE_MOBILE_LINEITEM_START_DATE]: makeMutation('startDate', 'mobileLineItem', true),
  [types.UPDATE_MOBILE_LINEITEM_END_DATE]: makeMutation('endDate', 'mobileLineItem', true),
  [types.UPDATE_MOBILE_LINEITEM_BUDGET]: makeMutation('budget', 'mobileLineItem', true),
  [types.UPDATE_MOBILE_LINEITEM_USE_LINEITEM_GEO_TARGETING]: makeMutation(
    'useLineItemGeoTargeting',
    'mobileLineItem',
    true,
  ),
  [types.UPDATE_MOBILE_LINEITEM_CREATIVES]: makeMutation('mobileLineItemCreatives', 'mobileLineItem', true),
  [types.UPDATE_MOBILE_LINEITEM_CREATIVE_IDS]: makeMutation('creativeIDs', 'mobileLineItem', true),
  [types.UPDATE_MOBILE_LINEITEM_LINEITEMS]: makeMutation('mobileLineItemLineItems', 'mobileLineItem', true),
  [types.UPDATE_MOBILE_LINEITEM_LINEITEM_IDS]: makeMutation('lineItemIDs', 'mobileLineItem', true),
  [types.UPDATE_MOBILE_LINEITEM_STATE]: makeMutation('state', 'mobileLineItem', true),
  [types.UPDATE_MOBILE_LINEITEM_NOTES]: makeMutation('notes', 'mobileLineItem', true),
  [types.UPDATE_MOBILE_LINEITEM_MAX_BID_CPM]: makeMutation('maxBidCPM', 'mobileLineItem', true),
  [types.UPDATE_MOBILE_LINEITEM_FREQ_CAP_COUNT]: makeMutation('freqCapCount', 'mobileLineItem', true),
  [types.UPDATE_MOBILE_LINEITEM_FREQ_CAP_PERIOD]: makeMutation('freqCapPeriod', 'mobileLineItem', true),
  [types.UPDATE_MOBILE_LINEITEM_FORMAT]: makeMutation('format', 'mobileLineItem', true),
  [types.UPDATE_MOBILE_LINEITEM_GOAL]: makeMutation('goal', 'mobileLineItem', true),

  // NETWORKS
  [types.UPDATE_NETWORK_NAME]: makeMutation('name', 'network', true),
  [types.UPDATE_NETWORK_CODE]: makeMutation('code', 'network', true),
  [types.UPDATE_NETWORK_SPOT_LENGTH]: makeMutation('spotLength', 'network', true),
  [types.UPDATE_NETWORK_LOOP_LENGTH]: makeMutation('loopLength', 'network', true),

  // ORGANIZATIONCUSTOMFIELDS
  [types.UPDATE_ORGANIZATIONCUSTOMFIELD_NAME]: makeMutation('name', 'organizationCustomField', true),
  [types.UPDATE_ORGANIZATIONCUSTOMFIELD_KEY]: makeMutation('key', 'organizationCustomField', true),
  [types.UPDATE_ORGANIZATIONCUSTOMFIELD_TYPE]: makeMutation('type', 'organizationCustomField', true),
  [types.UPDATE_ORGANIZATIONCUSTOMFIELD_IS_REQUIRED]: makeMutation('isRequired', 'organizationCustomField', true),

  // OWNER_YIELD_MANAGER_SSPS
  [types.UPDATE_OWNER_YIELD_MANAGER_SSP_NAME]: makeMutation('name', 'ownerYieldManagerSsp', true),
  [types.UPDATE_OWNER_YIELD_MANAGER_SSP_DOMAIN]: makeMutation('domain', 'ownerYieldManagerSsp', true),
  [types.UPDATE_OWNER_YIELD_MANAGER_SSP_RESPONSETIMEOUT]: makeMutation('responseTimeout', 'ownerYieldManagerSsp', true),
  [types.UPDATE_OWNER_YIELD_MANAGER_SSP_NOTES]: makeMutation('notes', 'ownerYieldManagerSsp', true),
  [types.UPDATE_OWNER_YIELD_MANAGER_SSP_ISACTIVE]: makeMutation('isActive', 'ownerYieldManagerSsp', true),
  [types.UPDATE_OWNER_YIELD_MANAGER_SSP_APIKEY]: makeMutation('apiKey', 'ownerYieldManagerSsp', true),

  // PACKAGES
  [types.UPDATE_PACKAGE_AD_DURATION_MAX]: makeMutation('adDurationMax', 'package', true),
  [types.UPDATE_PACKAGE_AD_DURATION_MIN]: makeMutation('adDurationMin', 'package', true),
  [types.UPDATE_PACKAGE_AD_DURATION_OVERRIDABLE]: makeMutation('adDurationOverridable', 'package', true),
  [types.UPDATE_PACKAGE_AUCTION_TYPE]: makeMutation('auctionType', 'package', true),
  [types.UPDATE_PACKAGE_BUDGET_MAX]: makeMutation('budgetMax', 'package', true),
  [types.UPDATE_PACKAGE_BUDGET_MIN]: makeMutation('budgetMin', 'package', true),
  [types.UPDATE_PACKAGE_BUDGET_MODE]: makeMutation('budgetMode', 'package', true),
  [types.UPDATE_PACKAGE_BUDGET_OVERRIDABLE]: makeMutation('budgetOverridable', 'package', true),
  [types.UPDATE_PACKAGE_BUYER_EXTERNAL_IDS]: makeMutation('buyerExternalIDs', 'package', true),
  [types.UPDATE_PACKAGE_BUYER_EXTERNAL_EXCHANGE_IDS]: makeMutation('buyerExternalExchangeIDs', 'package', true),
  [types.UPDATE_PACKAGE_BUYER_ORGANIZATION_DSP_IDS]: makeMutation('buyerOrganizationDSPIDs', 'package', true),
  [types.UPDATE_PACKAGE_BUYER_ORGANIZATION_DSPS]: makeMutation('buyerOrganizationDSPs', 'package', true),
  [types.UPDATE_PACKAGE_CADENCE_MAX]: makeMutation('cadenceMax', 'package', true),
  [types.UPDATE_PACKAGE_CADENCE_MIN]: makeMutation('cadenceMin', 'package', true),
  [types.UPDATE_PACKAGE_CADENCE_SMOOTH_CATCHUP]: makeMutation('cadenceSmoothCatchup', 'package', true),
  [types.UPDATE_PACKAGE_CO2_EMISSIONS_SCORE_THRESHOLD]: makeMutation('co2EmissionsScoreThreshold', 'package', true),
  [types.UPDATE_PACKAGE_CPM_FIXED]: makeMutation('cpmFixed', 'package', true),
  [types.UPDATE_PACKAGE_CPM_FROM_RATE_CARD]: makeMutation('isCPMFromRateCard', 'package', true),
  [types.UPDATE_PACKAGE_CREATIVEFORMATEXCLUSIONS]: makeMutation('creativeFormatExclusions', 'package', true),
  [types.UPDATE_PACKAGE_CREATIVEFORMATS]: makeMutation('creativeFormats', 'package', true),
  [types.UPDATE_PACKAGE_CREATIVEORIENTATIONEXCLUSIONS]: makeMutation('creativeOrientationExclusions', 'package', true),
  [types.UPDATE_PACKAGE_CREATIVEORIENTATIONS]: makeMutation('creativeOrientations', 'package', true),
  [types.UPDATE_PACKAGE_CUSTOM_EVENT_TARGETING_OVERRIDABLE]: makeMutation(
    'customEventTargetingOverridable',
    'package',
    true,
  ),
  [types.UPDATE_PACKAGE_CURATOR_ORGANIZATION_IDS]: makeMutation('curatorOrganizationIDs', 'package', true),
  [types.UPDATE_PACKAGE_DAYPART]: makeMutation('daypart', 'package', true),
  [types.UPDATE_PACKAGE_DAYPART_OVERRIDABLE]: makeMutation('daypartOverridable', 'package', true),
  [types.UPDATE_PACKAGE_DEALID]: makeMutation('dealID', 'package', true),
  [types.UPDATE_PACKAGE_DELIVERY_STATS]: makeMutation('deliveryStats', 'package', false),
  [types.UPDATE_PACKAGE_DIRECT_BILLING]: makeMutation('directBilling', 'package', true),
  [types.UPDATE_PACKAGE_DEMOGRAPHIC_TARGET_IDS]: makeMutation('demographicTargetIDs', 'package', true),
  [types.UPDATE_PACKAGE_DEMOGRAPHIC_TARGETS_OVERRIDABLE]: makeMutation(
    'demographicTargetsOverridable',
    'package',
    true,
  ),
  [types.UPDATE_PACKAGE_ENDDATE]: makeMutation('endDate', 'package', true, (val) => DateTime.fromISO(val)),
  [types.UPDATE_PACKAGE_ESTIMATED_AUDIENCE_CPM]: makeMutation('estimatedAudienceCPM', 'package'),
  [types.UPDATE_PACKAGE_ESTIMATED_AVERAGE_AUDIENCE_CPM]: makeMutation('estimatedAverageAudienceCPM', 'package'),
  [types.UPDATE_PACKAGE_ESTIMATED_AVERAGE_CPM]: makeMutation('estimatedAverageCPM', 'package'),
  [types.UPDATE_PACKAGE_ESTIMATED_CPM]: makeMutation('estimatedCPM', 'package'),
  [types.UPDATE_PACKAGE_ESTIMATED_CPM]: makeMutation('estimatedCPM', 'package'),
  [types.UPDATE_PACKAGE_ENTITY_FEES]: makeMutation('entityFees', 'package'),
  [types.UPDATE_PACKAGE_GEOFENCE_GROUP_IDS]: makeMutation('geofenceGroupIDs', 'package', true),
  [types.UPDATE_PACKAGE_GEOFENCE_GROUP_EXCLUSION_IDS]: makeMutation('geofenceGroupExclusionIDs', 'package', true),
  [types.UPDATE_PACKAGE_HIDE_UNIT_INFORMATION_IN_BID_REQUESTS]: makeMutation(
    'hideUnitInformationInBidRequests',
    'package',
    true,
  ),
  [types.UPDATE_PACKAGE_IABCATEGORIES]: makeMutation('iabCategories', 'package', true),
  [types.UPDATE_PACKAGE_IABCATEGORYEXCLUSIONS]: makeMutation('iabCategoryExclusions', 'package', true),
  [types.UPDATE_PACKAGE_ID]: makeMutation('id', 'package', false),
  [types.UPDATE_PACKAGE_IS_EVERGREEN]: makeMutation('isEvergreen', 'package', true),
  [types.UPDATE_PACKAGE_IS_FOR_BIDDING]: makeMutation('isForBidding', 'package', true),
  [types.UPDATE_PACKAGE_IS_OPEN]: makeMutation('isOpen', 'package', true),
  [types.UPDATE_PACKAGE_LANGUAGE_EXCLUSION_IDS]: makeMutation('languageExclusionIDs', 'package', true),
  [types.UPDATE_PACKAGE_LANGUAGE_IDS]: makeMutation('languageIDs', 'package', true),
  [types.UPDATE_PACKAGE_LOCATION_EXCLUSION_IDS]: makeMutation('locationExclusionIDs', 'package', true),
  [types.UPDATE_PACKAGE_LOCATION_TARGET_IDS]: makeMutation('locationTargetIDs', 'package', true),
  [types.UPDATE_PACKAGE_LOCATION_TARGETS_OVERRIDABLE]: makeMutation('locationTargetsOverridable', 'package', true),
  [types.UPDATE_PACKAGE_MARKET_IDS]: makeMutation('marketIDs', 'package', true),
  [types.UPDATE_PACKAGE_MARKET_TARGETED_IMPRESSIONS_MAX]: makeMutation('marketTargetedImpressionsMax', 'package', true),
  [types.UPDATE_PACKAGE_MARKET_TARGETED_IMPRESSIONS_MIN]: makeMutation('marketTargetedImpressionsMin', 'package', true),
  [types.UPDATE_PACKAGE_MARKET_TARGETED_IMPRESSIONS_OVERRIDABLE]: makeMutation(
    'marketTargetedImpressionsOverridable',
    'package',
    true,
  ),
  [types.UPDATE_PACKAGE_MEDIA_OWNER_IDS]: makeMutation('mediaOwnerIDs', 'package'),
  [types.UPDATE_PACKAGE_MEDIA_TYPE_IDS]: makeMutation('mediaTypeIDs', 'package'),
  [types.UPDATE_PACKAGE_MEDIA_TYPES_OVERRIDABLE]: makeMutation('mediaTypesOverridable', 'package'),
  [types.UPDATE_PACKAGE_MIN_CONCENTRATION_FACTOR]: makeMutation('minConcentrationFactor', 'package'),
  [types.UPDATE_PACKAGE_MIN_CONCENTRATION_VALUE]: makeMutation('minConcentrationValue', 'package'),
  [types.UPDATE_PACKAGE_MIN_SECONDS_BETWEEN_PLAYS_MAX]: makeMutation('minSecondsBetweenPlaysMax', 'package', true),
  [types.UPDATE_PACKAGE_MIN_SECONDS_BETWEEN_PLAYS_MIN]: makeMutation('minSecondsBetweenPlaysMin', 'package', true),
  [types.UPDATE_PACKAGE_MIN_SECONDS_BETWEEN_PLAYS_OVERRIDABLE]: makeMutation(
    'minSecondsBetweenPlaysOverridable',
    'package',
    true,
  ),
  [types.UPDATE_PACKAGE_NAME]: makeMutation('name', 'package', true),
  [types.UPDATE_PACKAGE_NETWORK_IDS]: makeMutation('networkIDs', 'package', true),
  [types.UPDATE_PACKAGE_NETWORKS_OVERRIDABLE]: makeMutation('networksOverridable', 'package', true),
  [types.UPDATE_PACKAGE_TARGETING_CRITERIA_OVERRIDABLE]: makeMutation(
    'packageTargetingCriteriaOverridable',
    'package',
    true,
  ),
  [types.UPDATE_PACKAGE_NOTES]: makeMutation('notes', 'package', true),
  [types.UPDATE_PACKAGE_NUMBER_OF_DAYS_MAX]: makeMutation('numberOfDaysMax', 'package', true),
  [types.UPDATE_PACKAGE_NUMBER_OF_DAYS_MIN]: makeMutation('numberOfDaysMin', 'package', true),
  [types.UPDATE_PACKAGE_NUMBER_OF_DAYS_OVERRIDABLE]: makeMutation('numberOfDaysOverridable', 'package', true),
  [types.UPDATE_PACKAGE_ORGANIZATION_ID]: makeMutation('organizationID', 'package', true),
  [types.UPDATE_PACKAGE_PACKAGE_IDS]: makeMutation('packageIDs', 'package', true),
  [types.UPDATE_PACKAGE_PACKAGES]: makeMutation('packages', 'package', true),
  [types.UPDATE_PACKAGE_PROPERTIES]: makeMutation('packageProperties', 'package', true),
  [types.UPDATE_PACKAGE_PURCHASE_TYPE]: makeMutation('purchaseType', 'package', true),
  [types.UPDATE_PACKAGE_PURCHASE_TYPE_OVERRIDABLE]: makeMutation('purchaseTypeOverridable', 'package', true),
  [types.UPDATE_PACKAGE_SITE_EXCLUSION_IDS]: makeMutation('siteExclusionIDs', 'package', true),
  [types.UPDATE_PACKAGE_SITE_IDS]: makeMutation('siteIDs', 'package', true),
  [types.UPDATE_PACKAGE_STARTDATE]: makeMutation('startDate', 'package', true, (val) => DateTime.fromISO(val)),
  [types.UPDATE_PACKAGE_STATE]: makeMutation('state', 'package', true),
  [types.UPDATE_PACKAGE_TARGETING_CRITERIA]: makeMutation('packageTargetingCriteria', 'package', true),
  [types.UPDATE_PACKAGE_TIMESTAMP]: makeMutation('timestamp', 'package'),
  [types.UPDATE_PACKAGE_UNIT_ASSIGNMENTS_OVERRIDABLE]: makeMutation('unitAssignmentsOverridable', 'package', true),
  [types.UPDATE_PACKAGE_UNIT_EXCLUSION_IDS]: makeMutation('unitExclusionIDs', 'package', true),
  [types.UPDATE_PACKAGE_UNIT_IDS]: makeMutation('unitIDs', 'package', true),
  [types.UPDATE_PACKAGE_UNITPACK_EXCLUSION_IDS]: makeMutation('unitPackExclusionIDs', 'package', true),
  [types.UPDATE_PACKAGE_UNITPACK_IDS]: makeMutation('unitPackIDs', 'package', true),
  [types.UPDATE_PACKAGE_UNITTAGS]: makeMutation('unitTags', 'package'),
  [types.UPDATE_PACKAGE_UNITTAGEXCLUSIONS]: makeMutation('unitTagExclusions', 'package'),
  [types.UPDATE_PACKAGE_PLAN_ID]: makeMutation('planID', 'package'),
  [types.UPDATE_PACKAGE_CURATION_TYPE]: makeMutation('curationType', 'package'),
  [types.UPDATE_PACKAGE_SPEND_STEERING]: makeMutation('spendSteering', 'package'),

  // PLANS
  [types.UPDATE_PLAN_ACCOUNT_ID]: makeMutation('accountID', 'plan', true),
  [types.UPDATE_PLAN_ADDURATION]: makeMutation('adDuration', 'plan', true),
  [types.UPDATE_PLAN_ADVERTISER_ID]: makeMutation('advertiserID', 'plan', true),
  [types.UPDATE_PLAN_BREAKDOWNTYPE]: makeMutation('breakdownType', 'plan', true),
  [types.UPDATE_PLAN_BUDGET]: makeMutation('budget', 'plan', true),
  [types.UPDATE_PLAN_BUDGET_MODE]: makeMutation('budgetMode', 'plan', true),
  [types.UPDATE_PLAN_BUY_FROM_DEALS_ENABLED]: makeMutation('buyFromDealsEnabled', 'plan', true),
  [types.UPDATE_PLAN_CPM]: makeMutation('CPM', 'plan', true),
  [types.UPDATE_PLAN_CPM_CALCULATOR]: makeMutation('cpmCalculator', 'plan', true),
  [types.UPDATE_PLAN_CREATIVEFORMATEXCLUSIONS]: makeMutation('creativeFormatExclusions', 'plan', true),
  [types.UPDATE_PLAN_CREATIVEFORMATS]: makeMutation('creativeFormats', 'plan', true),
  [types.UPDATE_PLAN_CREATIVEORIENTATIONEXCLUSIONS]: makeMutation('creativeOrientationExclusions', 'plan', true),
  [types.UPDATE_PLAN_CREATIVEORIENTATIONS]: makeMutation('creativeOrientations', 'plan', true),
  [types.UPDATE_PLAN_DAYPART]: makeMutation('daypart', 'plan', true),
  [types.UPDATE_PLAN_DEMOGRAPHIC_TARGET_IDS]: makeMutation('demographicIDs', 'plan', true),
  [types.UPDATE_PLAN_ENDDATE]: makeMutation('endDate', 'plan', true),
  [types.UPDATE_PLAN_ESTIMATED_AUDIENCE_CPM]: makeMutation('estimatedAudienceCPM', 'plan', true),
  [types.UPDATE_PLAN_ESTIMATED_AVERAGE_AUDIENCE_CPM]: makeMutation('estimatedAverageAudienceCPM', 'plan', true),
  [types.UPDATE_PLAN_ESTIMATED_AVERAGE_CPM]: makeMutation('estimatedAverageCPM', 'plan', true),
  [types.UPDATE_PLAN_ESTIMATED_CPM]: makeMutation('estimatedCPM', 'plan', true),
  [types.UPDATE_PLAN_GEOFENCE_GROUP_IDS]: makeMutation('geofenceGroupIDs', 'plan', true),
  [types.UPDATE_PLAN_GEOFENCE_GROUP_EXCLUSION_IDS]: makeMutation('geofenceGroupExclusionIDs', 'plan', true),
  [types.UPDATE_PLAN_GOAL]: makeMutation('goal', 'plan', true),
  [types.UPDATE_PLAN_HEATMAPS]: makeMutation('heatmaps', 'plan', true),
  [types.UPDATE_PLAN_IABCATEGORIES]: makeMutation('iabCategories', 'plan', true),
  [types.UPDATE_PLAN_IABCATEGORYEXCLUSIONS]: makeMutation('iabCategoryExclusions', 'plan', true),
  [types.UPDATE_PLAN_IS_CPM_FROM_RATE_CARD]: makeMutation('isCPMFromRateCard', 'plan', true),
  [types.UPDATE_PLAN_IS_MEASUREMENT_REQUESTED]: makeMutation('isMeasurementRequested', 'plan', true),
  [types.UPDATE_PLAN_LOCATIONEXCLUSIONIDS]: makeMutation('locationExclusionIDs', 'plan', true),
  [types.UPDATE_PLAN_LOCATIONIDS]: makeMutation('locationIDs', 'plan', true),
  [types.UPDATE_PLAN_MAXBIDCPM]: makeMutation('maxBidCPM', 'plan', true),
  [types.UPDATE_PLAN_MARKET_IDS]: makeMutation('marketIDs', 'plan', true),
  [types.UPDATE_PLAN_MARKETEXCLUSIONIDS]: makeMutation('marketExclusionIDs', 'plan', true),
  [types.UPDATE_PLAN_MEDIA_OWNER_IDS]: makeMutation('mediaOwnerIDs', 'plan', true),
  [types.UPDATE_PLAN_MEDIATYPEEXCLUSIONIDS]: makeMutation('mediaTypeExclusionIDs', 'plan', true),
  [types.UPDATE_PLAN_MEDIATYPEIDS]: makeMutation('mediaTypeIDs', 'plan', true),
  [types.UPDATE_PLAN_MIN_CONCENTRATION_FACTOR]: makeMutation('minConcentrationFactor', 'plan', true),
  [types.UPDATE_PLAN_MIN_CONCENTRATION_VALUE]: makeMutation('minConcentrationValue', 'plan', true),
  [types.UPDATE_PLAN_MINSECONDSBETWEENPLAYS]: makeMutation('minSecondsBetweenPlays', 'plan', true),
  [types.UPDATE_PLAN_NAME]: makeMutation('name', 'plan', true),
  [types.UPDATE_PLAN_NETWORKEXCLUSIONIDS]: makeMutation('networkExclusionIDs', 'plan', true),
  [types.UPDATE_PLAN_NETWORKFREQUENCYCAPS]: makeMutation('networkFrequencyCaps', 'plan', true),
  [types.UPDATE_PLAN_NETWORKIDS]: makeMutation('networkIDs', 'plan', true),
  [types.UPDATE_PLAN_PACKAGE]: makeMutation('packageInternal', 'plan', true),
  [types.UPDATE_PLAN_PACKAGEGROUP_DEALS]: makeMutation('packageGroupDealsIDs', 'plan', true),
  [types.UPDATE_PLAN_PACKAGEGROUP_ID]: makeMutation('packageGroupID', 'plan', true),
  [types.UPDATE_PLAN_PACKAGE_ID]: makeMutation('packageID', 'plan', true),
  [types.UPDATE_PLAN_PLANFEE]: makeMutation('planFee', 'plan', true),
  [types.UPDATE_PLAN_PROPERTIES]: makeMutation('planProperties', 'plan', true),
  [types.UPDATE_PLAN_PURCHASE_TYPE]: makeMutation('purchaseType', 'plan', true),
  [types.UPDATE_PLAN_SITEEXCLUSIONIDS]: makeMutation('siteExclusionIDs', 'plan', true),
  [types.UPDATE_PLAN_SITEIDS]: makeMutation('siteIDs', 'plan', true),
  [types.UPDATE_PLAN_STARTDATE]: makeMutation('startDate', 'plan', true),
  [types.UPDATE_PLAN_TARGETIMPRESSIONS]: makeMutation('targetImpressions', 'plan', true),
  [types.UPDATE_PLAN_TARGETING_CRITERIA]: makeMutation('planTargetingCriteria', 'plan', true),
  [types.UPDATE_PLAN_TTD_CAMPAIGN]: makeMutation('ttdCampaign', 'plan', true),
  [types.UPDATE_PLAN_UNITEXCLUSIONIDS]: makeMutation('unitExclusionIDs', 'plan', true),
  [types.UPDATE_PLAN_UNITIDS]: makeMutation('unitIDs', 'plan', true),
  [types.UPDATE_PLAN_UNITPACKEXCLUSIONIDS]: makeMutation('unitPackExclusionIDs', 'plan', true),
  [types.UPDATE_PLAN_UNITPACKIDS]: makeMutation('unitPackIDs', 'plan', true),
  [types.UPDATE_PLAN_UNITTAGEXCLUSIONS]: makeMutation('unitTagExclusions', 'plan'),
  [types.UPDATE_PLAN_UNITTAGS]: makeMutation('unitTags', 'plan'),
  [types.UPDATE_PLAN_JOURNEY_ID]: makeMutation('journeyWorkflowConfigurationId', 'plan', true),

  // POSTAL CODES FILE
  [types.UPDATE_POSTAL_CODES_FILE_NAME]: makeMutation('name', 'postalCodesFile', true),

  // PRESALE HEATMAPS
  [types.UPDATE_PRESALEHEATMAP_DESCRIPTION]: makeMutation('description', 'preSaleHeatmap', true),
  [types.UPDATE_PRESALEHEATMAP_NAME]: makeMutation('name', 'preSaleHeatmap', true),
  [types.UPDATE_PRESALEHEATMAP_PUBLIC_PLAINTEXT_PASSWORD]: makeMutation(
    'publicPlaintextPassword',
    'preSaleHeatmap',
    true,
  ),
  [types.UPDATE_PRESALEHEATMAP_JSON]: makeMutation('json', 'preSaleHeatmap', true),

  // DATA PROVIDER
  [types.UPDATE_DATAPROVIDER_EXTERNALDEMOGRAPHICS]: makeMutation('externalDemographics', 'dataProvider', true),
  [types.UPDATE_DATAPROVIDER_SELECTEDEXTERNALDEMOGRAPHICS]: makeMutation(
    'selectedExternalDemographics',
    'dataProvider',
    true,
  ),

  // REPORT DEFINITIONS
  [types.UPDATE_REPORTDEFINITION_NAME]: makeMutation('name', 'reportDefinition', true),
  [types.UPDATE_REPORTDEFINITION_PREFERRED_RUNNER_TYPE]: makeMutation('preferredRunnerType', 'reportDefinition', true),
  [types.UPDATE_REPORTDEFINITION_TIMEZONE]: makeMutation('definition.timezone', 'reportDefinition', true),
  [types.UPDATE_REPORTDEFINITION_BREAKDOWN]: makeMutation('definition.breakdown', 'reportDefinition', true),
  [types.UPDATE_REPORTDEFINITION_DIMENSIONS]: makeMutation('definition.dimensions', 'reportDefinition', true),
  [types.UPDATE_REPORTDEFINITION_EMAILTARGETS]: makeMutation('emailTargets', 'reportDefinition', true),
  [types.UPDATE_REPORTDEFINITION_REPORTSCHEDULES]: makeMutation('reportSchedules', 'reportDefinition', true),
  [types.UPDATE_REPORTDEFINITION_FILTERS]: makeMutation('definition.filters', 'reportDefinition', true),
  [types.UPDATE_REPORTDEFINITION_METRICS]: makeMutation('definition.metrics', 'reportDefinition', true),
  [types.UPDATE_REPORTDEFINITION_DATES]: makeMutation('definition.dates', 'reportDefinition', true),
  [types.UPDATE_REPORTDEFINITION_LASTEXECUTED_ON](state, reportDefinition) {
    reportDefinition.lastExecutedOnUtc = DateTime.local().toUTC();
  },
  [types.UPDATE_REPORTDEFINITION_DEFINITION_FORMAT]: makeMutation('definition.format', 'reportDefinition', true),
  [types.UPDATE_REPORTDEFINITION_DEFAULT_OUTPUT_FORMAT]: makeMutation(
    'defaultReportOutputFormat',
    'reportDefinition',
    true,
  ),

  // REPORT SCHEDULES
  [types.UPDATE_REPORTSCHEDULE_DAYOFMONTH]: makeMutation('dayOfMonth', 'reportSchedule', true),
  [types.UPDATE_REPORTSCHEDULE_EXECUTIONTIMEZONE]: makeMutation('executionTimezone', 'reportSchedule', true),
  [types.UPDATE_REPORTSCHEDULE_FIRSTEVENT]: makeMutation('firstEvent', 'reportSchedule', true),
  [types.UPDATE_REPORTSCHEDULE_LASTEVENT]: makeMutation('lastEvent', 'reportSchedule', true),
  [types.UPDATE_REPORTSCHEDULE_RECURRINGPERIOD]: makeMutation('recurringPeriod', 'reportSchedule', true),

  // SITES
  [types.UPDATE_SITE_NAME]: makeMutation('name', 'site', true),
  [types.UPDATE_SITE_EXTERNALID]: makeMutation('externalID', 'site', true),
  [types.UPDATE_SITE_DESCRIPTION]: makeMutation('description', 'site', true),
  [types.UPDATE_SITE_ISACTIVE]: makeMutation('isActive', 'site', true),
  [types.UPDATE_SITE_ISSTREETVIEWIMAGEAVAILABLE]: makeMutation('isStreetViewImageAvailable', 'site', true),
  [types.UPDATE_SITE_LATITUDE]: makeMutation('latitude', 'site', true),
  [types.UPDATE_SITE_LONGITUDE]: makeMutation('longitude', 'site', true),
  [types.UPDATE_SITE_LOCATIONID]: makeMutation('locationID', 'site', true),
  [types.UPDATE_SITE_ACTIVE]: makeMutation('active', 'site', true),
  [types.UPDATE_SITE_UNITS]: makeMutation('units', 'site', true),

  // UNITS
  [types.UPDATE_UNIT_AD_SERVER_AVAILABLITY]: makeMutation('availableForAdServer', 'unit', true),
  [types.UPDATE_UNIT_ALLOWHTML]: makeMutation('allowHtml', 'unit', true),
  [types.UPDATE_UNIT_ALLOWIMAGE]: makeMutation('allowImage', 'unit', true),
  [types.UPDATE_UNIT_ALLOWVIDEO]: makeMutation('allowVideo', 'unit', true),
  [types.UPDATE_UNIT_ALLOWZIP]: makeMutation('allowZip', 'unit', true),
  [types.UPDATE_UNIT_ALLOWAUDIO]: makeMutation('allowAudio', 'unit', true),
  [types.UPDATE_UNIT_BLOCKUNIQUEADVERTISERIDS]: makeMutation('blockUniqueAdvertiserIDs', 'unit', true),
  [types.UPDATE_UNIT_DEAL_AVAILABILITY]: makeMutation('availableForDeals', 'unit', true),
  [types.UPDATE_UNIT_DESCRIPTION]: makeMutation('description', 'unit', true),
  [types.UPDATE_UNIT_ENABLE_STRICT_FREQUENCY_CAPPING]: makeMutation('enableStrictIABFrequencyCapping', 'unit', true),
  [types.UPDATE_UNIT_ENABLE_STRICT_IAB_BLOCK_CATEGORIES]: makeMutation(
    'enableStrictIABCategoriesBlocking',
    'unit',
    true,
  ),
  [types.UPDATE_UNIT_EXTERNALID]: makeMutation('externalID', 'unit', true, (value) => value.trim()),
  [types.UPDATE_UNIT_FACING_DIRECTION]: makeMutation('facingDirection', 'unit', true),
  [types.UPDATE_UNIT_FALLBACK_CREATIVES]: makeMutation('fallbackCreatives', 'unit', true),
  [types.UPDATE_UNIT_FLOORCPM]: makeMutation('floorCPM', 'unit', true),
  [types.UPDATE_UNIT_GEOFENCE]: makeMutation('geofence', 'unit', true),
  [types.UPDATE_UNIT_IAB_BLOCK_CATEGORIES]: makeMutation('iabBlockCategories', 'unit', true),
  [types.UPDATE_UNIT_IMAGE_URI]: makeMutation('imageUri', 'unit', true),
  [types.UPDATE_UNIT_IMPRESSION_VERIFIER_VENDOR]: makeMutation('impressionVerifierVendor', 'unit', true),
  [types.UPDATE_UNIT_IMPRESSION_VERIFIER_VENDOR_EXTERNAL_ID]: makeMutation(
    'impressionVerifierVendorExternalId',
    'unit',
    true,
  ),
  [types.UPDATE_UNIT_ISACTIVE]: makeMutation('isActive', 'unit', true),
  [types.UPDATE_UNIT_ISSTREETVIEWIMAGEAVAILABLE]: makeMutation('isStreetViewImageAvailable', 'unit', true),
  [types.UPDATE_UNIT_LATITUDE]: makeMutation('latitude', 'unit', true),
  [types.UPDATE_UNIT_LOCATIONID]: makeMutation('locationID', 'unit', true),
  [types.UPDATE_UNIT_LONGITUDE]: makeMutation('longitude', 'unit', true),
  [types.UPDATE_UNIT_LOOPLENGTH]: makeMutation('loopLength', 'unit', true),
  [types.UPDATE_UNIT_MAX_SPOTLENGTH]: makeMutation('maxSpotLength', 'unit', true),
  [types.UPDATE_UNIT_MEDIATYPEID]: makeMutation('mediaTypeID', 'unit', true),
  [types.UPDATE_UNIT_MIN_SECONDS_BETWEEN_AD_DOMAIN_PLAYS]: makeMutation('minSecondsBetweenAdDomainPlays', 'unit', true),
  [types.UPDATE_UNIT_MIN_SECONDS_BETWEEN_IAB_CATEGORY_PLAYS]: makeMutation(
    'minSecondsBetweenIABCategoryPlays',
    'unit',
    true,
  ),
  [types.UPDATE_UNIT_MIN_SPOTLENGTH]: makeMutation('minSpotLength', 'unit', true),
  [types.UPDATE_UNIT_NAME]: makeMutation('name', 'unit', true),
  [types.UPDATE_UNIT_NETWORKID]: makeMutation('networkID', 'unit', true),
  [types.UPDATE_UNIT_OPEN_EXCHANGE_AVAILABILITY]: makeMutation('availableForOpenExchange', 'unit', true),
  [types.UPDATE_UNIT_OPERATINGHOURS]: makeMutation('operatingHours', 'unit', true),
  [types.UPDATE_UNIT_PHYSICAL_SCREEN_HEIGHT]: makeMutation('physicalScreenHeight', 'unit', true),
  [types.UPDATE_UNIT_PHYSICAL_SCREEN_WIDTH]: makeMutation('physicalScreenWidth', 'unit', true),
  [types.UPDATE_UNIT_PROPERTIES]: makeMutation('properties', 'unit', true),
  [types.UPDATE_UNIT_SCREENHEIGHT]: makeMutation('screenHeight', 'unit', true),
  [types.UPDATE_UNIT_SCREENWIDTH]: makeMutation('screenWidth', 'unit', true),
  [types.UPDATE_UNIT_SITE]: makeMutation('site', 'unit', true),
  [types.UPDATE_UNIT_SITEID]: makeMutation('siteID', 'unit', true),
  [types.UPDATE_UNIT_SPOTLENGTH]: makeMutation('spotLength', 'unit', true),
  [types.UPDATE_UNIT_TAGS]: makeMutation('tags', 'unit', true),
  [types.UPDATE_UNIT_STORE_FRONT_AVIALABILTY]: makeMutation('availableForStoreFront', 'unit', true),
  [types.UPDATE_UNIT_TIMEZONE]: makeMutation('timezone', 'unit', true),
  [types.UPDATE_UNIT_UNITLANGUAGES]: makeMutation('unitLanguages', 'unit', true),
  [types.UPDATE_UNIT_UUID]: makeMutation('uuid', 'unit', true),
  [types.UPDATE_UNIT_PHYSICAL_UNIT_COUNT]: makeMutation('physicalUnitCount', 'unit', true),
  [types.UPDATE_UNIT_HAS_PHYSICAL_UNIT_COUNT]: makeMutation('hasPhysicalUnitCount', 'unit', true),

  // UNIT PACKS
  [types.UPDATE_UNITPACK_DESCRIPTION]: makeMutation('description', 'unitPack', true),
  [types.UPDATE_UNITPACK_NAME]: makeMutation('name', 'unitPack', true),
  [types.UPDATE_UNITPACK_ISACTIVE]: makeMutation('isActive', 'unitPack', true),
  [types.UPDATE_UNITPACK_EXTERNALID]: makeMutation('externalID', 'unitPack', true),

  // USER
  [types.UPDATE_USER_PROFILEPICTURE]: makeMutation('profilePictureUrl', 'user', true),

  // USER ORGANIZATIONS
  [types.UPDATE_USER_ORGANIZATION_ROLEID]: makeMutation('roleID', 'userOrganization', true),

  // USER SETTINGS
  [types.UPDATE_USERSETTING_KEY]: makeMutation('key', 'userSetting', true),
  [types.UPDATE_USERSETTING_VALUE]: makeMutation('value', 'userSetting', true),

  // USER PROFILE
  [types.UPDATE_USERPROFILE_DISPLAYNAME]: makeMutation('displayName', 'userProfile', true),
};

export default mutations;
